const instances = [];
const DEG_TO_RAD = Math.PI / 180;
const DEFAULT_TEXTURE_URL = 'https://assets.science.nasa.gov/content/dam/science/esd/eo/images/bmng/bmng-topography/january/world.topo.200401.3x5400x2700.jpg';

const clamp = (value, min, max) => Math.min(Math.max(Number(value) || 0, min), max);

const freezeRect = (rect) => (rect ? ({
    height: rect.height,
    left: rect.left,
    top: rect.top,
    width: rect.width,
}) : null);

const createElement = (tagName, className, text) => {
    const element = document.createElement(tagName);

    if (className) {
        element.className = className;
    }

    if (text !== undefined) {
        element.textContent = text;
    }

    return element;
};

const createIconButton = (className, icon, label) => {
    const button = createElement('button', className);
    button.type = 'button';
    button.setAttribute('aria-label', label);
    button.title = label;
    button.textContent = icon;

    return button;
};

const parseBatchImages = (batchElement) => {
    const jsonElement = batchElement.querySelector('.zgg-media-map-batch__json');

    if (!jsonElement) {
        return [];
    }

    try {
        const images = JSON.parse(jsonElement.textContent || '[]');

        return Array.isArray(images) ? images : [];
    } catch (error) {
        return [];
    }
};

const parseLocations = (root) => Array.from(root.querySelectorAll('.zgg-media-map-location')).map((locationElement, index) => {
    const batches = Array.from(locationElement.querySelectorAll('.zgg-media-map-batch')).map((batchElement) => ({
        name: batchElement.dataset.batchName || 'Batch',
        backgroundColor: batchElement.dataset.backgroundColor || '#101826',
        textColor: batchElement.dataset.textColor || '#f7fbff',
        images: parseBatchImages(batchElement),
    }));

    return {
        id: `location-${index}`,
        name: locationElement.dataset.locationName || 'Location',
        latitude: Number(locationElement.dataset.latitude) || 0,
        longitude: Number(locationElement.dataset.longitude) || 0,
        batches,
    };
});

const firstImageForLocation = (location) => {
    const batch = location.batches.find((item) => item.images.length);

    return batch ? batch.images[0] : null;
};

const firstBatchForLocation = (location) => location.batches[0] || null;

const isVideoMedia = (media) => media?.type === 'video' || String(media?.mime || '').startsWith('video/');

const mediaPreviewSource = (media) => media?.poster || media?.thumb || media?.large || media?.full || media?.url || '';

const mediaFullSource = (media) => media?.full || media?.large || media?.url || mediaPreviewSource(media);

const mediaLabel = (media, fallback = '') => media?.caption || media?.title || media?.alt || fallback;

const mediaCountLabel = (count) => (count === 1 ? '1 Medium' : `${count} Medien`);

const isValidLinkTitle = (media) => {
    if (!media?.titleIsLink || !media?.title) {
        return false;
    }

    try {
        const url = new URL(media.title, window.location.href);

        return ['http:', 'https:', 'mailto:', 'tel:'].includes(url.protocol);
    } catch (error) {
        return false;
    }
};

const appendLinkedCaption = (container, media, fallback = '') => {
    container.replaceChildren();
    container.classList.remove('has-link');
    const label = mediaLabel(media, fallback);

    if (!label) {
        return false;
    }

    if (isValidLinkTitle(media)) {
        const link = createElement('a', null, media.title);
        link.href = media.title;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        container.classList.add('has-link');
        container.append(link);
        return true;
    }

    container.textContent = label;
    return true;
};

const createMediaPreview = (media, className, fallbackLabel = '') => {
    const preview = mediaPreviewSource(media);
    const isVideo = isVideoMedia(media);
    const wrapper = createElement('span', `${className} ${isVideo ? 'is-video' : ''}`.trim());

    if (preview) {
        const image = createElement('img');
        image.src = preview;
        image.alt = media?.alt || media?.title || fallbackLabel;
        image.draggable = false;
        wrapper.append(image);
    } else {
        wrapper.append(createElement('span', 'zgg-media-map__media-placeholder', isVideo ? 'Video' : 'Medium'));
    }

    if (isVideo) {
        wrapper.append(createElement('span', 'zgg-media-map__media-play'));
    }

    return wrapper;
};

const imagesWithBatchTheme = (images, batch) => (Array.isArray(images) ? images : []).map((image) => ({
    ...image,
    backgroundColor: image.backgroundColor || batch?.backgroundColor || '#101826',
    textColor: image.textColor || batch?.textColor || '#f7fbff',
}));

const imagesForLocation = (location) => location.batches.reduce(
    (images, batch) => images.concat(imagesWithBatchTheme(batch.images, batch)),
    []
);

const createShader = (gl, type, source) => {
    const shader = gl.createShader(type);
    gl.shaderSource(shader, source);
    gl.compileShader(shader);

    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        throw new Error(gl.getShaderInfoLog(shader) || 'Globe shader could not compile.');
    }

    return shader;
};

const createProgram = (gl, vertexSource, fragmentSource) => {
    const program = gl.createProgram();
    gl.attachShader(program, createShader(gl, gl.VERTEX_SHADER, vertexSource));
    gl.attachShader(program, createShader(gl, gl.FRAGMENT_SHADER, fragmentSource));
    gl.linkProgram(program);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
        throw new Error(gl.getProgramInfoLog(program) || 'Globe program could not link.');
    }

    return program;
};

const createSphere = (columns = 96, rows = 48) => {
    const positions = [];
    const uvs = [];
    const indices = [];

    for (let row = 0; row <= rows; row += 1) {
        const v = row / rows;
        const latitude = Math.PI / 2 - v * Math.PI;
        const cosLat = Math.cos(latitude);

        for (let column = 0; column <= columns; column += 1) {
            const u = column / columns;
            const longitude = u * Math.PI * 2 - Math.PI;

            positions.push(
                Math.cos(latitude) * Math.sin(longitude),
                Math.sin(latitude),
                cosLat * Math.cos(longitude)
            );
            uvs.push(u, v);
        }
    }

    for (let row = 0; row < rows; row += 1) {
        for (let column = 0; column < columns; column += 1) {
            const first = row * (columns + 1) + column;
            const second = first + columns + 1;

            indices.push(first, second, first + 1);
            indices.push(second, second + 1, first + 1);
        }
    }

    return {
        positions: new Float32Array(positions),
        uvs: new Float32Array(uvs),
        indices: new Uint16Array(indices),
    };
};

const multiplyMatrix3 = (a, b) => ([
    a[0] * b[0] + a[1] * b[3] + a[2] * b[6],
    a[0] * b[1] + a[1] * b[4] + a[2] * b[7],
    a[0] * b[2] + a[1] * b[5] + a[2] * b[8],
    a[3] * b[0] + a[4] * b[3] + a[5] * b[6],
    a[3] * b[1] + a[4] * b[4] + a[5] * b[7],
    a[3] * b[2] + a[4] * b[5] + a[5] * b[8],
    a[6] * b[0] + a[7] * b[3] + a[8] * b[6],
    a[6] * b[1] + a[7] * b[4] + a[8] * b[7],
    a[6] * b[2] + a[7] * b[5] + a[8] * b[8],
]);

const toColumnMajor = (matrix) => new Float32Array([
    matrix[0], matrix[3], matrix[6],
    matrix[1], matrix[4], matrix[7],
    matrix[2], matrix[5], matrix[8],
]);

const createRotationMatrix = (centerLatitude, centerLongitude) => {
    const lat = centerLatitude * DEG_TO_RAD;
    const lng = -centerLongitude * DEG_TO_RAD;
    const cosLat = Math.cos(lat);
    const sinLat = Math.sin(lat);
    const cosLng = Math.cos(lng);
    const sinLng = Math.sin(lng);
    const rotateY = [
        cosLng, 0, sinLng,
        0, 1, 0,
        -sinLng, 0, cosLng,
    ];
    const rotateX = [
        1, 0, 0,
        0, cosLat, -sinLat,
        0, sinLat, cosLat,
    ];

    return multiplyMatrix3(rotateX, rotateY);
};

const rotateVector = (latitude, longitude, rotationMatrix) => {
    const lat = latitude * DEG_TO_RAD;
    const lng = longitude * DEG_TO_RAD;
    const vector = [
        Math.cos(lat) * Math.sin(lng),
        Math.sin(lat),
        Math.cos(lat) * Math.cos(lng),
    ];

    return {
        x: rotationMatrix[0] * vector[0] + rotationMatrix[1] * vector[1] + rotationMatrix[2] * vector[2],
        y: rotationMatrix[3] * vector[0] + rotationMatrix[4] * vector[1] + rotationMatrix[5] * vector[2],
        z: rotationMatrix[6] * vector[0] + rotationMatrix[7] * vector[1] + rotationMatrix[8] * vector[2],
    };
};

class GlobeRenderer {
    constructor(canvas, textureUrl) {
        this.canvas = canvas;
        this.textureUrl = textureUrl || DEFAULT_TEXTURE_URL;
        this.gl = canvas.getContext('webgl', { alpha: true, antialias: true });

        if (!this.gl) {
            throw new Error('WebGL is not available.');
        }

        this.setup();
    }

    setup() {
        const gl = this.gl;
        const vertexSource = `
            attribute vec3 a_position;
            attribute vec2 a_uv;
            uniform mat3 u_rotation;
            uniform float u_aspect;
            uniform float u_zoom;
            varying vec2 v_uv;
            varying float v_depth;
            varying float v_light;

            void main() {
                vec3 rotated = u_rotation * a_position;
                vec2 projected = rotated.xy * u_zoom;

                if (u_aspect > 1.0) {
                    projected.x = projected.x / u_aspect;
                } else {
                    projected.y = projected.y * u_aspect;
                }

                gl_Position = vec4(projected, -rotated.z * 0.5, 1.0);
                v_uv = a_uv;
                v_depth = rotated.z;
                v_light = clamp(0.45 + 0.55 * dot(normalize(rotated), normalize(vec3(-0.25, 0.18, 1.0))), 0.0, 1.0);
            }
        `;
        const fragmentSource = `
            precision mediump float;
            uniform sampler2D u_texture;
            varying vec2 v_uv;
            varying float v_depth;
            varying float v_light;

            void main() {
                if (v_depth < -0.03) {
                    discard;
                }

                vec4 color = texture2D(u_texture, v_uv);
                float edge = smoothstep(-0.02, 0.35, v_depth);
                vec3 atmosphere = vec3(0.035, 0.115, 0.22);
                vec3 shaded = color.rgb * v_light;

                gl_FragColor = vec4(mix(atmosphere, shaded, edge), smoothstep(-0.02, 0.05, v_depth));
            }
        `;

        this.program = createProgram(gl, vertexSource, fragmentSource);
        this.locations = {
            position: gl.getAttribLocation(this.program, 'a_position'),
            uv: gl.getAttribLocation(this.program, 'a_uv'),
            rotation: gl.getUniformLocation(this.program, 'u_rotation'),
            aspect: gl.getUniformLocation(this.program, 'u_aspect'),
            zoom: gl.getUniformLocation(this.program, 'u_zoom'),
            texture: gl.getUniformLocation(this.program, 'u_texture'),
        };

        const sphere = createSphere();
        this.indexCount = sphere.indices.length;
        this.positionBuffer = gl.createBuffer();
        this.uvBuffer = gl.createBuffer();
        this.indexBuffer = gl.createBuffer();

        gl.bindBuffer(gl.ARRAY_BUFFER, this.positionBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, sphere.positions, gl.STATIC_DRAW);
        gl.bindBuffer(gl.ARRAY_BUFFER, this.uvBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, sphere.uvs, gl.STATIC_DRAW);
        gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.indexBuffer);
        gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, sphere.indices, gl.STATIC_DRAW);

        this.texture = gl.createTexture();
        gl.bindTexture(gl.TEXTURE_2D, this.texture);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array([20, 42, 72, 255]));

        gl.enable(gl.DEPTH_TEST);
        gl.enable(gl.BLEND);
        gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

        this.textureReady = false;
    }

    loadTexture(onLoad, onError) {
        const image = new Image();
        image.crossOrigin = 'anonymous';
        image.onload = () => {
            const gl = this.gl;
            try {
                const textureSource = this.createTextureSource(image);
                gl.bindTexture(gl.TEXTURE_2D, this.texture);
                gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, textureSource);
                this.textureReady = true;
                onLoad?.();
            } catch (error) {
                onError?.();
            }
        };
        image.onerror = () => onError?.();
        image.src = this.textureUrl;
    }

    createTextureSource(image) {
        const gl = this.gl;
        const hardwareLimit = gl.getParameter(gl.MAX_TEXTURE_SIZE) || 2048;
        const maxTextureSize = Math.min(hardwareLimit, 4096);
        const width = image.naturalWidth || image.width;
        const height = image.naturalHeight || image.height;

        if (!width || !height || (width <= maxTextureSize && height <= maxTextureSize)) {
            return image;
        }

        const scale = Math.min(maxTextureSize / width, maxTextureSize / height);
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');

        canvas.width = Math.max(1, Math.floor(width * scale));
        canvas.height = Math.max(1, Math.floor(height * scale));
        context.drawImage(image, 0, 0, canvas.width, canvas.height);

        return canvas;
    }

    resize() {
        const rect = this.canvas.getBoundingClientRect();
        const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
        const width = Math.max(1, Math.floor(rect.width * pixelRatio));
        const height = Math.max(1, Math.floor(rect.height * pixelRatio));

        if (this.canvas.width !== width || this.canvas.height !== height) {
            this.canvas.width = width;
            this.canvas.height = height;
        }
    }

    render(rotationMatrix, zoom) {
        const gl = this.gl;
        this.resize();
        gl.viewport(0, 0, this.canvas.width, this.canvas.height);
        gl.clearColor(0, 0, 0, 0);
        gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
        gl.useProgram(this.program);

        gl.bindBuffer(gl.ARRAY_BUFFER, this.positionBuffer);
        gl.enableVertexAttribArray(this.locations.position);
        gl.vertexAttribPointer(this.locations.position, 3, gl.FLOAT, false, 0, 0);

        gl.bindBuffer(gl.ARRAY_BUFFER, this.uvBuffer);
        gl.enableVertexAttribArray(this.locations.uv);
        gl.vertexAttribPointer(this.locations.uv, 2, gl.FLOAT, false, 0, 0);

        gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.indexBuffer);
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, this.texture);
        gl.uniform1i(this.locations.texture, 0);
        gl.uniformMatrix3fv(this.locations.rotation, false, toColumnMajor(rotationMatrix));
        gl.uniform1f(this.locations.aspect, this.canvas.width / this.canvas.height);
        gl.uniform1f(this.locations.zoom, zoom);
        gl.drawElements(gl.TRIANGLES, this.indexCount, gl.UNSIGNED_SHORT, 0);
    }
}

class MediaMap {
    constructor(root) {
        this.root = root;
        this.viewport = root.querySelector('[data-map-viewport]');
        this.canvas = root.querySelector('[data-globe-canvas]');
        this.loader = root.querySelector('[data-globe-loader]');
        this.markers = root.querySelector('[data-map-markers]');
        this.panel = root.querySelector('[data-map-panel]');
        this.locations = parseLocations(root);
        this.scale = 0.92;
        this.maxZoom = 3.6;
        this.centerLatitude = 0;
        this.centerLongitude = 0;
        this.dragStart = null;
        this.activePointers = new Map();
        this.pinchStart = null;
        this.selectedLocation = null;
        this.currentView = 'map';
        this.historyActive = false;
        this.needsRender = true;
        this.animationFrame = null;
        this.focusAnimationFrame = null;
        this.imageViewer = null;
        this.imageViewerKeyHandler = null;
        this.imageViewerImages = [];
        this.imageViewerIndex = 0;
        this.imageViewerChangeId = 0;
        this.isTouchGlobeInteraction = false;
        this.dragStartPoint = null;
        this.suppressNextMarkerClick = false;

        if (!this.viewport || !this.canvas || !this.markers || !this.panel) {
            return;
        }

        this.textureUrl = root.dataset.globeTexture || DEFAULT_TEXTURE_URL;
        this.maxZoom = this.parseMaxZoom(root.dataset.mapMaxZoom);
        this.setupGlobe();
        this.renderMarkers();
        this.bindMapControls();
        this.applyTransform();
    }

    setupGlobe() {
        try {
            this.globe = new GlobeRenderer(this.canvas, this.textureUrl);
            this.globe.loadTexture(
                () => {
                    this.root.classList.add('is-texture-loaded');
                    if (this.loader) {
                        this.loader.hidden = true;
                    }
                    this.scheduleRender();
                },
                () => {
                    this.root.classList.add('has-texture-error');
                    if (this.loader) {
                        this.loader.textContent = 'Globus-Textur konnte nicht geladen werden.';
                    }
                }
            );

            if ('ResizeObserver' in window) {
                this.resizeObserver = new ResizeObserver(() => {
                    this.scheduleRender();
                    this.updateMarkerPositions();
                });
                this.resizeObserver.observe(this.viewport);
            } else {
                window.addEventListener('resize', () => {
                    this.scheduleRender();
                    this.updateMarkerPositions();
                });
            }
        } catch (error) {
            this.root.classList.add('has-webgl-error');
            if (this.loader) {
                this.loader.textContent = 'WebGL ist für den Globus nicht verfügbar.';
            }
        }
    }

    isAlbumOpen() {
        return this.currentView === 'album';
    }

    getRotationMatrix() {
        return createRotationMatrix(this.centerLatitude, this.centerLongitude);
    }

    renderMarkers() {
        this.markers.replaceChildren();

        if (!this.locations.length) {
            this.panel.hidden = false;
            this.panel.replaceChildren(createElement('p', 'zgg-media-map__empty', 'Keine Orte hinterlegt.'));
            return;
        }

        this.locations.forEach((location) => {
            const button = createElement('button', 'zgg-media-map__marker');
            const previewImage = firstImageForLocation(location);
            const firstBatch = firstBatchForLocation(location);

            button.type = 'button';
            button.setAttribute('aria-label', location.name);
            button.style.setProperty('--zgg-media-map-location-background', firstBatch?.backgroundColor || '#101826');
            button.style.setProperty('--zgg-media-map-location-text', firstBatch?.textColor || '#f7fbff');

            const bubble = createElement('span', 'zgg-media-map__marker-bubble');

            if (previewImage) {
                bubble.append(createMediaPreview(previewImage, 'zgg-media-map__marker-preview', location.name));
            }

            bubble.append(createElement('span', 'zgg-media-map__marker-name', location.name));
            button.append(bubble, createElement('span', 'zgg-media-map__marker-pin'));
            button.addEventListener('click', (event) => {
                if (this.suppressNextMarkerClick) {
                    event.preventDefault();
                    this.suppressNextMarkerClick = false;
                    return;
                }

                this.renderBatchOverview(location);
            });
            button.addEventListener('dragstart', (event) => event.preventDefault());
            button.zggLocation = location;

            this.markers.append(button);
        });

        this.updateMarkerPositions();
    }

    renderBatchOverview(location, animateFromBubble = true, sourceRectOverride = null) {
        const sourceRect = sourceRectOverride || (animateFromBubble ? this.getLocationBubbleRect(location) : null);

        this.selectedLocation = location;
        this.currentView = 'batches';
        this.historyActive = false;
        this.root.classList.add('has-open-panel');
        this.setActiveLocation(location);
        this.panel.hidden = false;
        this.panel.className = 'zgg-media-map__panel zgg-media-map__panel--batches';
        this.panel.classList.toggle('is-preparing', !!sourceRect);
        this.setPanelTheme(firstBatchForLocation(location));
        this.panel.replaceChildren();

        const header = createElement('div', 'zgg-media-map__panel-header');
        const titleGroup = createElement('div', 'zgg-media-map__panel-title');
        const totalImages = location.batches.reduce((sum, batch) => sum + batch.images.length, 0);
        const closeButton = createIconButton('zgg-media-map__icon-button zgg-media-map__close', '×', 'Schliessen');

        closeButton.addEventListener('click', () => this.closePanel());
        titleGroup.append(createElement('span', 'zgg-media-map__eyebrow', 'Location'), createElement('h3', null, location.name));
        header.append(titleGroup, closeButton);
        this.panel.append(header, this.renderLocationHero(location, totalImages));

        if (!location.batches.length) {
            this.panel.append(createElement('p', 'zgg-media-map__empty', 'Keine Batches hinterlegt.'));
            this.focusLocation(location, animateFromBubble);
            this.animatePanelFromRect(sourceRect, !!sourceRect);
            return;
        }

        const list = createElement('div', 'zgg-media-map__batch-list');

        location.batches.forEach((batch) => {
            const button = createElement('button', 'zgg-media-map__batch');
            const preview = batch.images[0];
            const count = batch.images.length;
            const text = createElement('span', 'zgg-media-map__batch-text');

            button.type = 'button';
            button.style.setProperty('--zgg-media-map-batch-background', batch.backgroundColor || '#101826');
            button.style.setProperty('--zgg-media-map-batch-text', batch.textColor || '#f7fbff');
            button.style.color = batch.textColor || '#f7fbff';
            text.style.color = batch.textColor || '#f7fbff';
            text.append(createElement('span', 'zgg-media-map__batch-name', batch.name));
            text.append(createElement('span', 'zgg-media-map__batch-count', mediaCountLabel(count)));
            button.classList.toggle('has-preview', !!preview);
            button.append(text);

            if (preview) {
                button.prepend(createMediaPreview(preview, 'zgg-media-map__batch-preview', batch.name));
            }

            button.addEventListener('click', () => this.renderAlbum(location, batch));
            list.append(button);
        });

        this.panel.append(list);
        this.focusLocation(location, animateFromBubble);
        this.animatePanelFromRect(sourceRect, !!sourceRect);
    }

    renderAlbum(location, batch) {
        const sourceRect = this.panel && !this.panel.hidden ? freezeRect(this.panel.getBoundingClientRect()) : null;

        this.selectedLocation = location;
        this.currentView = 'album';
        this.root.classList.add('has-open-panel');
        this.setActiveLocation(location);
        this.panel.hidden = false;
        this.panel.className = 'zgg-media-map__panel zgg-media-map__panel--album';
        this.panel.classList.toggle('is-preparing', !!sourceRect);
        this.setPanelTheme(batch);
        this.panel.replaceChildren();
        this.focusLocation(location, false);

        if (!this.historyActive && window.history?.pushState) {
            window.history.pushState({ zggMediaMapAlbum: true }, '', window.location.href);
            this.historyActive = true;
        }

        const header = createElement('div', 'zgg-media-map__panel-header');
        const backButton = createIconButton('zgg-media-map__icon-button zgg-media-map__back', '←', 'Zurück');
        const titleGroup = createElement('div', 'zgg-media-map__panel-title');
        const closeButton = createIconButton('zgg-media-map__icon-button zgg-media-map__close', '×', 'Schliessen');

        backButton.addEventListener('click', () => {
            if (this.historyActive) {
                window.history.back();
                return;
            }

            this.renderBatchOverview(
                location,
                false,
                this.panel && !this.panel.hidden ? freezeRect(this.panel.getBoundingClientRect()) : null
            );
        });
        closeButton.addEventListener('click', () => this.closePanel());

        titleGroup.append(
            createElement('span', 'zgg-media-map__eyebrow', location.name),
            createElement('h3', null, batch.name),
            createElement('span', 'zgg-media-map__panel-meta', mediaCountLabel(batch.images.length))
        );
        header.append(backButton, titleGroup, closeButton);
        this.panel.append(header);

        if (!batch.images.length) {
            this.panel.append(createElement('p', 'zgg-media-map__empty', 'Keine Medien hinterlegt.'));
            this.animatePanelFromRect(sourceRect, !!sourceRect);
            return;
        }

        const album = createElement('div', 'zgg-media-map__album');

        const themedImages = imagesWithBatchTheme(batch.images, batch);

        batch.images.forEach((image, index) => {
            const figure = createElement('figure', 'zgg-media-map__album-item');
            const button = createElement('button', 'zgg-media-map__album-trigger');
            const isVideo = isVideoMedia(image);
            const themedImage = themedImages[index];

            button.type = 'button';
            button.setAttribute('aria-label', image.alt || image.title || batch.name);
            button.append(createMediaPreview(image, 'zgg-media-map__album-preview', batch.name));
            button.addEventListener('click', () => {
                if (isVideo) {
                    window.open(mediaFullSource(image), '_blank', 'noopener,noreferrer');
                    return;
                }

                this.openImageViewer(themedImages, index, button);
            });
            figure.append(button);

            if (mediaLabel(image)) {
                const caption = createElement('figcaption');
                appendLinkedCaption(caption, themedImage, batch.name);
                figure.append(caption);
            }

            album.append(figure);
        });

        this.panel.append(album);
        this.animatePanelFromRect(sourceRect, !!sourceRect);
    }

    renderLocationHero(location, totalImages) {
        const hero = createElement(totalImages ? 'button' : 'div', 'zgg-media-map__location-hero');
        const preview = firstImageForLocation(location);
        const firstBatch = firstBatchForLocation(location);
        const copy = createElement('span', 'zgg-media-map__location-copy');
        const allImages = imagesForLocation(location);

        hero.classList.toggle('has-preview', !!preview);
        hero.style.color = firstBatch?.textColor || '#f7fbff';

        if (preview) {
            hero.append(createMediaPreview(preview, 'zgg-media-map__location-preview', location.name));
        }

        copy.append(
            createElement('span', 'zgg-media-map__panel-meta', location.batches.length === 1 ? '1 Batch' : `${location.batches.length} Batches`),
            createElement('span', 'zgg-media-map__panel-meta', mediaCountLabel(totalImages))
        );

        if (totalImages) {
            hero.type = 'button';
            hero.setAttribute('aria-label', `Alle Medien von ${location.name} öffnen`);
            hero.addEventListener('click', () => this.openImageViewer(allImages, 0, hero));
            hero.addEventListener('dragstart', (event) => event.preventDefault());
        }

        hero.append(copy);

        return hero;
    }

    setPanelTheme(batch = null) {
        const textColor = batch?.textColor || '#f7fbff';

        this.panel.style.setProperty('--zgg-media-map-panel-background', batch?.backgroundColor || '#101826');
        this.panel.style.setProperty('--zgg-media-map-panel-text', textColor);
    }

    openImageViewer(images, selectedIndex, sourceElement) {
        const viewerImages = (Array.isArray(images) ? images : [images]).filter(Boolean);

        if (!viewerImages.length) {
            return;
        }

        const sourceRect = sourceElement ? freezeRect(sourceElement.getBoundingClientRect()) : null;
        const overlay = createElement('div', 'zgg-media-map__lightbox');
        const shell = createElement('div', 'zgg-media-map__lightbox-shell');
        const closeButton = createIconButton('zgg-media-map__icon-button zgg-media-map__lightbox-close', '×', 'Schliessen');
        const previousButton = createIconButton('zgg-media-map__icon-button zgg-media-map__lightbox-nav zgg-media-map__lightbox-prev', '‹', 'Vorheriges Bild');
        const nextButton = createIconButton('zgg-media-map__icon-button zgg-media-map__lightbox-nav zgg-media-map__lightbox-next', '›', 'Nächstes Bild');
        const media = createElement('div', 'zgg-media-map__lightbox-media');
        const largeImage = createElement('img', 'zgg-media-map__lightbox-image');
        const videoPlaceholder = createElement('div', 'zgg-media-map__lightbox-video-placeholder', 'Video');
        const videoLink = createElement('a', 'zgg-media-map__lightbox-video-link', 'Video öffnen');
        const caption = createElement('div', 'zgg-media-map__lightbox-caption');

        this.closeImageViewer(false);
        this.imageViewerImages = viewerImages;
        this.imageViewerIndex = clamp(selectedIndex, 0, viewerImages.length - 1);
        overlay.setAttribute('role', 'dialog');
        overlay.setAttribute('aria-modal', 'true');
        overlay.setAttribute('aria-label', 'Bildansicht');
        closeButton.addEventListener('click', () => this.closeImageViewer());
        previousButton.addEventListener('click', () => this.showImageViewerImage(this.imageViewerIndex - 1, -1));
        nextButton.addEventListener('click', () => this.showImageViewerImage(this.imageViewerIndex + 1, 1));
        overlay.addEventListener('click', (event) => {
            if (event.target === overlay) {
                this.closeImageViewer();
            }
        });

        if (viewerImages.length < 2) {
            previousButton.hidden = true;
            nextButton.hidden = true;
        }

        videoLink.target = '_blank';
        videoLink.rel = 'noopener noreferrer';
        videoLink.hidden = true;
        videoPlaceholder.hidden = true;
        media.append(largeImage, videoPlaceholder, videoLink);
        shell.append(closeButton, previousButton, nextButton, media, caption);
        overlay.append(shell);
        this.viewport.append(overlay);
        this.imageViewer = overlay;
        this.showImageViewerImage(this.imageViewerIndex, 0, false);
        this.imageViewerKeyHandler = (event) => {
            if (event.key === 'Escape') {
                this.closeImageViewer();
                return;
            }

            if (event.key === 'ArrowLeft') {
                this.showImageViewerImage(this.imageViewerIndex - 1, -1);
                return;
            }

            if (event.key === 'ArrowRight') {
                this.showImageViewerImage(this.imageViewerIndex + 1, 1);
            }
        };
        window.addEventListener('keydown', this.imageViewerKeyHandler);
        closeButton.focus({ preventScroll: true });
        this.animateElementFromRect(shell, sourceRect);
    }

    showImageViewerImage(nextIndex, direction = 0, animate = true) {
        if (!this.imageViewer || !this.imageViewerImages.length) {
            return;
        }

        const total = this.imageViewerImages.length;
        const normalizedIndex = ((nextIndex % total) + total) % total;
        const image = this.imageViewerImages[normalizedIndex];
        const shell = this.imageViewer.querySelector('.zgg-media-map__lightbox-shell');
        const largeImage = this.imageViewer.querySelector('.zgg-media-map__lightbox-image');
        const videoPlaceholder = this.imageViewer.querySelector('.zgg-media-map__lightbox-video-placeholder');
        const videoLink = this.imageViewer.querySelector('.zgg-media-map__lightbox-video-link');
        const caption = this.imageViewer.querySelector('.zgg-media-map__lightbox-caption');
        const isVideo = isVideoMedia(image);
        const changeId = this.imageViewerChangeId + 1;

        if (!shell || !largeImage || !videoPlaceholder || !videoLink || !caption) {
            return;
        }

        this.imageViewerIndex = normalizedIndex;
        this.imageViewerChangeId = changeId;

        const update = (animateResize = false) => {
            const beforeRect = animateResize ? freezeRect(shell.getBoundingClientRect()) : null;
            const source = isVideo ? mediaPreviewSource(image) : mediaFullSource(image);
            const videoUrl = isVideo ? mediaFullSource(image) : '';

            this.setImageViewerTheme(image);
            shell.classList.toggle('is-video', isVideo);
            largeImage.hidden = !source;
            if (source) {
                largeImage.src = source;
            } else {
                largeImage.removeAttribute('src');
            }
            largeImage.alt = image.alt || image.title || '';
            videoPlaceholder.hidden = !isVideo || !!source;
            videoLink.hidden = !isVideo || !videoUrl;
            if (videoUrl) {
                videoLink.href = videoUrl;
            } else {
                videoLink.removeAttribute('href');
            }
            const hasCaption = appendLinkedCaption(caption, image);
            caption.hidden = !hasCaption;
            shell.classList.toggle('has-caption', hasCaption);

            if (!animateResize || !beforeRect) {
                return Promise.resolve();
            }

            const mediaReady = source ? this.waitForImageReady(largeImage) : Promise.resolve();

            return mediaReady.then(() => {
                if (this.imageViewerChangeId !== changeId) {
                    return;
                }

                this.animateImageViewerResize(shell, beforeRect);
            });
        };

        if (!animate || !largeImage?.animate || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
            largeImage.style.opacity = '';
            update();
            return;
        }

        largeImage.getAnimations?.().forEach((animation) => animation.cancel());
        largeImage.style.opacity = '';
        const outAnimation = largeImage.animate([
            { opacity: 1, transform: 'translate3d(0, 0, 0) scale3d(1, 1, 1)' },
            { opacity: 0.24, offset: 0.68, transform: `translate3d(${direction * -10}px, 0, 0) scale3d(0.986, 0.986, 1)` },
            { opacity: 0, transform: `translate3d(${direction * -20}px, 0, 0) scale3d(0.972, 0.972, 1)` },
        ], {
            duration: 220,
            easing: 'cubic-bezier(0.25, 0.7, 0.25, 1)',
        });

        outAnimation.finished.then(() => {
            if (this.imageViewerChangeId !== changeId) {
                return;
            }

            largeImage.style.opacity = '0';
            update(true).then(() => {
                if (this.imageViewerChangeId !== changeId) {
                    return;
                }

                const inAnimation = largeImage.animate([
                    { opacity: 0, transform: `translate3d(${direction * 18}px, 0, 0) scale3d(0.976, 0.976, 1)` },
                    { opacity: 1, offset: 0.68, transform: `translate3d(${direction * -4}px, 0, 0) scale3d(1.032, 1.032, 1)` },
                    { opacity: 1, offset: 0.86, transform: `translate3d(${direction}px, 0, 0) scale3d(0.996, 0.996, 1)` },
                    { opacity: 1, transform: 'translate3d(0, 0, 0) scale3d(1, 1, 1)' },
                ], {
                    duration: 460,
                    easing: 'cubic-bezier(0.2, 0.78, 0.18, 1)',
                });

                inAnimation.finished.finally(() => {
                    if (this.imageViewerChangeId === changeId) {
                        largeImage.style.opacity = '';
                    }
                });
            });
        }).catch(() => {
            largeImage.style.opacity = '';
            update();
        });
    }

    waitForImageReady(image) {
        if (image.complete && image.naturalWidth) {
            return image.decode ? image.decode().catch(() => {}) : Promise.resolve();
        }

        return new Promise((resolve) => {
            const done = () => {
                image.removeEventListener('load', done);
                image.removeEventListener('error', done);
                resolve();
            };

            image.addEventListener('load', done, { once: true });
            image.addEventListener('error', done, { once: true });

            if (image.complete) {
                done();
            }
        });
    }

    animateImageViewerResize(shell, beforeRect) {
        if (!shell?.animate || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
            return;
        }

        window.requestAnimationFrame(() => {
            const afterRect = freezeRect(shell.getBoundingClientRect());

            if (!beforeRect?.width || !beforeRect?.height || !afterRect?.width || !afterRect?.height) {
                return;
            }

            const beforeCenterY = beforeRect.top + beforeRect.height * 0.5;
            const afterCenterY = afterRect.top + afterRect.height * 0.5;
            const scaleY = clamp(beforeRect.height / afterRect.height, 0.28, 1.8);
            const deltaY = beforeCenterY - afterCenterY;

            shell.animate([
                {
                    transform: `translate3d(0, ${deltaY}px, 0) scale3d(1, ${scaleY}, 1)`,
                },
                {
                    offset: 0.74,
                    transform: 'translate3d(0, 0, 0) scale3d(1, 1.018, 1)',
                },
                {
                    offset: 0.9,
                    transform: 'translate3d(0, 0, 0) scale3d(1, 0.997, 1)',
                },
                {
                    transform: 'translate3d(0, 0, 0) scale3d(1, 1, 1)',
                },
            ], {
                duration: 650,
                easing: 'cubic-bezier(0.2, 0.78, 0.18, 1)',
            });
        });
    }

    setImageViewerTheme(image = null) {
        if (!this.imageViewer) {
            return;
        }

        const textColor = image?.textColor || '#f7fbff';

        this.imageViewer.style.setProperty('--zgg-media-map-lightbox-background', image?.backgroundColor || '#101826');
        this.imageViewer.style.setProperty('--zgg-media-map-lightbox-text', textColor);
    }

    closeImageViewer(animate = true) {
        if (!this.imageViewer) {
            return;
        }

        const overlay = this.imageViewer;
        const shell = overlay.querySelector('.zgg-media-map__lightbox-shell');
        const remove = () => {
            overlay.remove();
        };

        if (this.imageViewerKeyHandler) {
            window.removeEventListener('keydown', this.imageViewerKeyHandler);
            this.imageViewerKeyHandler = null;
        }

        this.imageViewer = null;
        this.imageViewerImages = [];
        this.imageViewerIndex = 0;
        this.imageViewerChangeId += 1;

        if (!animate || !shell?.animate) {
            remove();
            return;
        }

        overlay.classList.add('is-closing');
        const animation = shell.animate([
            {
                opacity: 1,
                transform: 'translate3d(0, 0, 0) scale3d(1, 1, 1)',
            },
            {
                opacity: 1,
                offset: 0.32,
                transform: 'translate3d(0, 0, 0) scale3d(1.018, 1.018, 1)',
            },
            {
                opacity: 0.72,
                offset: 0.58,
                transform: 'translate3d(0, 0, 0) scale3d(0.985, 0.985, 1)',
            },
            {
                opacity: 0,
                transform: 'translate3d(0, 0, 0) scale3d(0.88, 0.88, 1)',
            },
        ], {
            duration: 420,
            easing: 'cubic-bezier(0.2, 0.78, 0.18, 1)',
        });

        animation.finished.then(remove).catch(remove);
    }

    backToBatchOverview() {
        if (!this.selectedLocation) {
            return;
        }

        const sourceRect = this.panel && !this.panel.hidden ? freezeRect(this.panel.getBoundingClientRect()) : null;

        this.historyActive = false;
        this.renderBatchOverview(this.selectedLocation, false, sourceRect);
    }

    closePanel() {
        this.currentView = 'map';
        this.historyActive = false;
        this.selectedLocation = null;
        this.closeImageViewer(false);
        this.root.classList.remove('has-open-panel');
        this.setActiveLocation(null);
        this.panel.hidden = true;
        this.panel.replaceChildren();
        this.panel.className = 'zgg-media-map__panel';
        this.setPanelTheme();
    }

    setActiveLocation(location) {
        Array.from(this.markers.children).forEach((marker) => {
            marker.classList.toggle('is-active', !!location && marker.zggLocation === location);
        });
        this.updateMarkerPositions();
    }

    getMarkerForLocation(location) {
        return Array.from(this.markers.children).find((marker) => marker.zggLocation === location) || null;
    }

    getLocationBubbleRect(location) {
        const marker = this.getMarkerForLocation(location);
        const bubble = marker?.querySelector('.zgg-media-map__marker-bubble');

        if (!bubble || marker.hidden) {
            return null;
        }

        const rect = bubble.getBoundingClientRect();

        return rect.width && rect.height ? freezeRect(rect) : null;
    }

    animatePanelFromRect(sourceRect, shouldAnimate = true) {
        if (!sourceRect || !shouldAnimate || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
            this.panel.classList.remove('is-preparing');
            return;
        }

        if (!this.panel.animate) {
            this.panel.classList.remove('is-preparing');
            return;
        }

        window.requestAnimationFrame(() => {
            const targetRect = this.panel.getBoundingClientRect();

            if (!sourceRect.width || !sourceRect.height || !targetRect.width || !targetRect.height) {
                this.panel.classList.remove('is-preparing');
                return;
            }

            const sourceCenterX = sourceRect.left + sourceRect.width * 0.5;
            const sourceCenterY = sourceRect.top + sourceRect.height * 0.5;
            const targetCenterX = targetRect.left + targetRect.width * 0.5;
            const targetCenterY = targetRect.top + targetRect.height * 0.5;
            const startScale = clamp(Math.min(sourceRect.width / targetRect.width, sourceRect.height / targetRect.height), 0.2, 1.35);
            const deltaX = sourceCenterX - targetCenterX;
            const deltaY = sourceCenterY - targetCenterY;
            const startTransform = `translate3d(${deltaX}px, ${deltaY}px, 0) scale3d(${startScale}, ${startScale}, 1)`;

            this.panel.getAnimations().forEach((animation) => animation.cancel());
            this.panel.classList.add('is-expanding');
            this.panel.style.transformOrigin = '50% 50%';
            this.panel.style.transform = startTransform;
            this.panel.style.opacity = '0.001';
            this.panel.offsetHeight;
            this.panel.classList.remove('is-preparing');
            this.panel.style.transform = '';
            this.panel.style.opacity = '';

            const animation = this.panel.animate([
                {
                    opacity: 0.001,
                    transform: startTransform,
                },
                {
                    opacity: 1,
                    offset: 0.7,
                    transform: 'translate3d(0, 0, 0) scale3d(1.036, 1.036, 1)',
                },
                {
                    opacity: 1,
                    offset: 0.88,
                    transform: 'translate3d(0, 0, 0) scale3d(0.992, 0.992, 1)',
                },
                {
                    opacity: 1,
                    transform: 'translate3d(0, 0, 0) scale3d(1, 1, 1)',
                },
            ], {
                duration: 840,
                easing: 'cubic-bezier(0.2, 0.78, 0.18, 1)',
            });

            const cleanup = () => {
                this.panel.classList.remove('is-expanding');
                this.panel.style.transformOrigin = '';
            };

            animation.finished.then(cleanup).catch(cleanup);
        });
    }

    animateElementFromRect(element, sourceRect) {
        if (!sourceRect || !element?.animate || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
            return;
        }

        window.requestAnimationFrame(() => {
            const targetRect = element.getBoundingClientRect();

            if (!sourceRect.width || !sourceRect.height || !targetRect.width || !targetRect.height) {
                return;
            }

            const sourceCenterX = sourceRect.left + sourceRect.width * 0.5;
            const sourceCenterY = sourceRect.top + sourceRect.height * 0.5;
            const targetCenterX = targetRect.left + targetRect.width * 0.5;
            const targetCenterY = targetRect.top + targetRect.height * 0.5;
            const startScale = clamp(Math.min(sourceRect.width / targetRect.width, sourceRect.height / targetRect.height), 0.18, 0.96);
            const startTransform = `translate3d(${sourceCenterX - targetCenterX}px, ${sourceCenterY - targetCenterY}px, 0) scale3d(${startScale}, ${startScale}, 1)`;

            element.style.transformOrigin = '50% 50%';
            element.animate([
                {
                    opacity: 0,
                    transform: startTransform,
                },
                {
                    opacity: 1,
                    offset: 0.7,
                    transform: 'translate3d(0, 0, 0) scale3d(1.026, 1.026, 1)',
                },
                {
                    opacity: 1,
                    offset: 0.88,
                    transform: 'translate3d(0, 0, 0) scale3d(0.994, 0.994, 1)',
                },
                {
                    opacity: 1,
                    transform: 'translate3d(0, 0, 0) scale3d(1, 1, 1)',
                },
            ], {
                duration: 660,
                easing: 'cubic-bezier(0.2, 0.78, 0.18, 1)',
            });
        });
    }

    focusLocation(location, animate = true, onComplete = null) {
        const targetLatitude = clamp(location.latitude, -70, 70);
        const targetLongitude = clamp(location.longitude, -180, 180);
        const targetScale = clamp(Math.max(this.scale, 1.55), 0.85, this.maxZoom);

        if (!animate || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
            this.cancelFocusAnimation(false);
            this.centerLatitude = targetLatitude;
            this.centerLongitude = targetLongitude;
            this.scale = targetScale;
            this.applyTransform();
            onComplete?.();
            return;
        }

        this.animateTransformTo(targetLatitude, targetLongitude, targetScale, onComplete);
    }

    animateTransformTo(targetLatitude, targetLongitude, targetScale, onComplete = null) {
        this.cancelFocusAnimation(false);

        const startLatitude = this.centerLatitude;
        const startLongitude = this.centerLongitude;
        const startScale = this.scale;
        const longitudeDelta = this.shortestLongitudeDelta(startLongitude, targetLongitude);
        const startTime = window.performance.now();
        const duration = 920;

        const tick = (time) => {
            const progress = clamp((time - startTime) / duration, 0, 1);
            const eased = this.easeOutBack(progress);

            this.centerLatitude = startLatitude + (targetLatitude - startLatitude) * eased;
            this.centerLongitude = this.normalizeLongitude(startLongitude + longitudeDelta * eased);
            this.scale = clamp(startScale + (targetScale - startScale) * eased, 0.85, this.maxZoom);
            this.applyTransform();

            if (progress < 1) {
                this.focusAnimationFrame = window.requestAnimationFrame(tick);
                return;
            }

            this.focusAnimationFrame = null;
            this.centerLatitude = targetLatitude;
            this.centerLongitude = targetLongitude;
            this.scale = targetScale;
            this.applyTransform();
            onComplete?.();
        };

        this.focusAnimationFrame = window.requestAnimationFrame(tick);
    }

    cancelFocusAnimation(revealPanel = true) {
        if (this.focusAnimationFrame) {
            window.cancelAnimationFrame(this.focusAnimationFrame);
            this.focusAnimationFrame = null;
        }

        if (revealPanel) {
            this.panel?.classList.remove('is-preparing');
        }
    }

    easeOutBack(progress) {
        const overshoot = 0.58;
        const shifted = progress - 1;

        return 1 + (overshoot + 1) * shifted * shifted * shifted + overshoot * shifted * shifted;
    }

    shortestLongitudeDelta(from, to) {
        let delta = to - from;

        while (delta > 180) {
            delta -= 360;
        }

        while (delta < -180) {
            delta += 360;
        }

        return delta;
    }

    bindMapControls() {
        const zoomIn = this.root.querySelector('[data-map-zoom-in]');
        const zoomOut = this.root.querySelector('[data-map-zoom-out]');
        const reset = this.root.querySelector('[data-map-reset]');

        zoomIn?.addEventListener('click', () => {
            this.cancelFocusAnimation();
            this.zoomBy(1.2);
        });
        zoomOut?.addEventListener('click', () => {
            this.cancelFocusAnimation();
            this.zoomBy(1 / 1.2);
        });
        reset?.addEventListener('click', () => {
            this.cancelFocusAnimation();
            this.resetTransform();
        });

        this.viewport.addEventListener('wheel', (event) => {
            if (this.isInterfaceEvent(event)) {
                return;
            }

            if (!this.isMarkerEvent(event) && !this.isPointInsideGlobe(event)) {
                return;
            }

            event.preventDefault();
            this.cancelFocusAnimation();
            const normalizedDelta = event.deltaMode === 1 ? event.deltaY * 16 : event.deltaY;
            const zoomDelta = clamp(-normalizedDelta, -240, 240);
            const zoomFactor = Math.exp(zoomDelta * 0.0018);
            this.zoomBy(zoomFactor);
        }, { passive: false });

        this.viewport.addEventListener('pointerdown', (event) => {
            if (this.isInterfaceEvent(event)) {
                return;
            }

            const markerTarget = event.target.closest('.zgg-media-map__marker');

            if (!markerTarget && !this.isPointInsideGlobe(event)) {
                return;
            }

            if (event.pointerType === 'touch') {
                event.preventDefault();
                this.isTouchGlobeInteraction = true;
            }

            this.cancelFocusAnimation();
            this.activePointers.set(event.pointerId, {
                clientX: event.clientX,
                clientY: event.clientY,
            });
            this.dragStartPoint = {
                clientX: event.clientX,
                clientY: event.clientY,
                location: markerTarget?.zggLocation || null,
                targetWasMarker: !!markerTarget,
            };
            this.suppressNextMarkerClick = false;

            if (event.pointerType === 'touch' && this.activePointers.size > 1) {
                this.startPinchGesture();
                this.viewport.classList.add('is-dragging');
                return;
            }

            this.dragStart = {
                pointerId: event.pointerId,
                pointerType: event.pointerType,
                clientX: event.clientX,
                clientY: event.clientY,
                latitude: this.centerLatitude,
                longitude: this.centerLongitude,
                isRotating: true,
            };

            this.capturePointer(event.pointerId);
            this.viewport.classList.add('is-dragging');
        });

        this.viewport.addEventListener('pointermove', (event) => {
            if (event.pointerType === 'touch' && this.isTouchGlobeInteraction) {
                event.preventDefault();
            }

            if (this.activePointers.has(event.pointerId)) {
                this.activePointers.set(event.pointerId, {
                    clientX: event.clientX,
                    clientY: event.clientY,
                });
            }

            if (this.pinchStart && this.activePointers.size > 1) {
                this.updatePinchGesture();
                return;
            }

            if (!this.dragStart || this.dragStart.pointerId !== event.pointerId) {
                return;
            }

            const deltaX = event.clientX - this.dragStart.clientX;
            const deltaY = event.clientY - this.dragStart.clientY;

            if (this.dragStartPoint?.targetWasMarker && Math.hypot(event.clientX - this.dragStartPoint.clientX, event.clientY - this.dragStartPoint.clientY) > 6) {
                this.suppressNextMarkerClick = true;
            }

            const sensitivity = 0.35 / this.scale;
            this.centerLongitude = this.normalizeLongitude(this.dragStart.longitude - deltaX * sensitivity);
            this.centerLatitude = clamp(this.dragStart.latitude + deltaY * sensitivity, -82, 82);
            this.applyTransform();
        });

        this.viewport.addEventListener('touchstart', (event) => {
            if (this.isInterfaceEvent(event)) {
                return;
            }

            if (!this.isTouchEventInsideGlobe(event)) {
                return;
            }

            this.isTouchGlobeInteraction = true;
            event.preventDefault();
        }, { passive: false });

        this.viewport.addEventListener('touchmove', (event) => {
            if (this.isTouchGlobeInteraction) {
                event.preventDefault();
            }
        }, { passive: false });

        ['touchend', 'touchcancel'].forEach((eventName) => {
            this.viewport.addEventListener(eventName, (event) => {
                if (!event.touches.length) {
                    this.isTouchGlobeInteraction = false;
                }
            });
        });

        ['pointerup', 'pointercancel'].forEach((eventName) => {
            this.viewport.addEventListener(eventName, (event) => this.endPointerInteraction(event));
        });
    }

    isInterfaceEvent(event) {
        return !!event.target.closest('a, input, textarea, select, [contenteditable="true"], [data-map-panel], .zgg-media-map__lightbox, .zgg-media-map__controls');
    }

    isMarkerEvent(event) {
        return !!event.target.closest('.zgg-media-map__marker');
    }

    isPointInsideGlobe(event) {
        return this.isClientPointInsideGlobe(event.clientX, event.clientY);
    }

    isTouchEventInsideGlobe(event) {
        return Array.from(event.touches).some((touch) => this.isClientPointInsideGlobe(touch.clientX, touch.clientY));
    }

    isClientPointInsideGlobe(clientX, clientY) {
        const rect = this.viewport.getBoundingClientRect();
        const radius = this.getGlobeRadius(rect) + 28;
        const centerX = rect.left + rect.width * 0.5;
        const centerY = rect.top + rect.height * 0.5;
        const deltaX = clientX - centerX;
        const deltaY = clientY - centerY;

        return Math.hypot(deltaX, deltaY) <= radius;
    }

    getGlobeRadius(rect = this.viewport.getBoundingClientRect()) {
        return Math.min(rect.width, rect.height) * 0.5 * this.scale;
    }

    updateGlobeGlow(rect = this.viewport.getBoundingClientRect()) {
        const glowSize = this.getGlobeRadius(rect) * 2.18;

        this.viewport.style.setProperty('--zgg-media-map-globe-glow-size', `${glowSize}px`);
    }

    capturePointer(pointerId) {
        try {
            this.viewport.setPointerCapture(pointerId);
        } catch (error) {
            // Pointer capture can fail if the browser already handed the gesture to scrolling.
        }
    }

    releasePointer(pointerId) {
        try {
            if (this.viewport.hasPointerCapture(pointerId)) {
                this.viewport.releasePointerCapture(pointerId);
            }
        } catch (error) {
            // Ignore browsers that do not expose capture state for this pointer anymore.
        }
    }

    startPinchGesture() {
        const pointers = Array.from(this.activePointers.values()).slice(0, 2);
        const distance = this.getPointerDistance(pointers);

        if (!distance) {
            return;
        }

        this.dragStart = null;
        this.pinchStart = {
            distance,
            scale: this.scale,
        };

        Array.from(this.activePointers.keys()).forEach((pointerId) => this.capturePointer(pointerId));
    }

    updatePinchGesture() {
        const pointers = Array.from(this.activePointers.values()).slice(0, 2);
        const distance = this.getPointerDistance(pointers);

        if (!this.pinchStart || !distance) {
            return;
        }

        this.setScale(this.pinchStart.scale * (distance / this.pinchStart.distance));
    }

    getPointerDistance(pointers) {
        if (pointers.length < 2) {
            return 0;
        }

        return Math.hypot(
            pointers[0].clientX - pointers[1].clientX,
            pointers[0].clientY - pointers[1].clientY
        );
    }

    endPointerInteraction(event) {
        const completedTapOnMarker = this.dragStartPoint?.targetWasMarker
            && this.dragStartPoint.location
            && !this.suppressNextMarkerClick
            && event.type !== 'pointercancel';

        this.activePointers.delete(event.pointerId);
        this.releasePointer(event.pointerId);

        if (this.dragStart?.pointerId === event.pointerId) {
            this.dragStart = null;
        }

        if (this.activePointers.size < 2) {
            this.pinchStart = null;
        }

        if (!this.dragStart && this.activePointers.size === 1 && event.type !== 'pointercancel') {
            const [pointerId, pointer] = Array.from(this.activePointers.entries())[0];

            this.dragStart = {
                pointerId,
                pointerType: 'touch',
                clientX: pointer.clientX,
                clientY: pointer.clientY,
                latitude: this.centerLatitude,
                longitude: this.centerLongitude,
                isRotating: true,
            };
        }

        if (!this.dragStart && this.activePointers.size === 0) {
            this.isTouchGlobeInteraction = false;
            if (completedTapOnMarker) {
                this.suppressNextMarkerClick = true;
                this.renderBatchOverview(this.dragStartPoint.location);
            }
            this.dragStartPoint = null;
            this.viewport.classList.remove('is-dragging');
        }
    }

    setScale(nextScale) {
        this.scale = clamp(nextScale, 0.85, this.maxZoom);
        this.applyTransform();
    }

    zoomBy(factor) {
        this.setScale(this.scale * factor);
    }

    parseMaxZoom(value) {
        const maxZoom = Number(value);

        return Number.isFinite(maxZoom) ? clamp(maxZoom, 1.2, 8) : 3.6;
    }

    resetTransform() {
        this.scale = 0.92;
        this.centerLatitude = 0;
        this.centerLongitude = 0;
        this.applyTransform();
    }

    normalizeLongitude(value) {
        let longitude = value;

        while (longitude > 180) {
            longitude -= 360;
        }

        while (longitude < -180) {
            longitude += 360;
        }

        return longitude;
    }

    applyTransform() {
        this.scheduleRender();
        this.updateMarkerPositions();
    }

    scheduleRender() {
        if (!this.globe || this.animationFrame) {
            return;
        }

        this.animationFrame = window.requestAnimationFrame(() => {
            this.animationFrame = null;
            this.globe.render(this.getRotationMatrix(), this.scale);
            this.updateMarkerPositions();
        });
    }

    updateMarkerPositions() {
        if (!this.markers) {
            return;
        }

        const rect = this.viewport.getBoundingClientRect();
        const radius = this.getGlobeRadius(rect);
        const rotationMatrix = this.getRotationMatrix();

        this.updateGlobeGlow(rect);

        Array.from(this.markers.children).forEach((marker) => {
            const location = marker.zggLocation;

            if (!location) {
                return;
            }

            const rotated = rotateVector(location.latitude, location.longitude, rotationMatrix);
            const left = rect.width * 0.5 + rotated.x * radius;
            const top = rect.height * 0.5 - rotated.y * radius;
            const visible = rotated.z > 0.05 && left > -80 && left < rect.width + 80 && top > -80 && top < rect.height + 80;
            const depthIndex = 100 + Math.round(rotated.z * 900);
            const activeIndex = marker.zggLocation === this.selectedLocation ? 1000 : 0;

            marker.style.left = `${left}px`;
            marker.style.top = `${top}px`;
            marker.hidden = !visible;
            marker.style.opacity = visible ? String(clamp(rotated.z * 1.8, 0.35, 1)) : '0';
            marker.style.zIndex = visible ? String(depthIndex + activeIndex) : '0';
        });
    }
}

const initMediaMaps = () => {
    document.querySelectorAll('[data-zgg-media-map]').forEach((root) => {
        if (root.zggMediaMap) {
            return;
        }

        root.zggMediaMap = new MediaMap(root);
        instances.push(root.zggMediaMap);
    });
};

window.addEventListener('popstate', () => {
    instances.forEach((instance) => {
        if (instance.isAlbumOpen()) {
            instance.backToBatchOverview();
        }
    });
});

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initMediaMaps);
} else {
    initMediaMaps();
}
